import { TitleButton } from '@/common/buttons/TitleButton';
import { KrDialog } from '@/common/dialog/Dialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { DialogFormFieldContainer } from '@/common/dialog/DialogFormFieldContainer';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { Grid } from '@/common/grid/Grid';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { TreeUtils } from '@/common/utils/TreeUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const parsePastedText = (text) => {
  return text
    .replace(/\r\n$/, '')
    .replace(/\n$/, '')
    .split(/\r?\n/)
    .map((line) => line.split('\t'));
};

const PasteDataDialog = ({ closeDialog }) => {
  const [text, setText] = useState('');

  return (
    <KrDialog
      title="Paste Data"
      subtitle="Paste tab-separated rows from a spreadsheet."
      fullWidth
      maxWidth="md"
      closeDialog={closeDialog}
      buttons={[
        <Button key="cancel" onClick={() => closeDialog()}>
          Cancel
        </Button>,
        <Button key="apply" onClick={() => closeDialog(text)} disabled={!text.trim()}>
          Apply
        </Button>,
      ]}
    >
      <TextField
        multiline
        minRows={8}
        maxRows={20}
        fullWidth
        placeholder="Paste spreadsheet data here..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoFocus
      />
    </KrDialog>
  );
};

const ErrorCellRenderer = (props) => {
  useEffect(() => {
    let hasError = props?.colDef?.hasError?.(props);

    props.eGridCell.style.backgroundColor = hasError ? '#ffcccc' : 'transparent';
  }, [props?.value, props?.node?.data?.[props?.colDef?.touchedField]]);

  return props.value;
};

const EntitySelectCellRenderer = (props) => {
  const { openDialog } = useDialog();

  const fieldDef = props.colDef;
  const codeField = props.colDef.field;
  const valueField = props.colDef.valueField;

  const [name, setName] = useState();

  const openEntitySelectDialog = () => {
    openDialog(EntitySelectDialog, {
      data: {
        module: fieldDef.module,
        entity: fieldDef.entity,
        rowSelection: fieldDef.rowSelection,
        field: fieldDef.field,
        expr: fieldDef.expr,
        query: fieldDef.query,
        replaceParams: fieldDef.replaceParams,
      },
    }).then((selectedEntities) => {
      props.colDef.setCellTouched?.(props);
      if (selectedEntities?.length > 0) {
        syncFromCode(selectedEntities?.[0]?.code);
      }
    });
  };

  useEffect(() => {
    syncFromCode(props.value);
  }, [props?.value, props?.node?.data?.[props?.colDef?.touchedField]]);

  const syncFromCode = (code) => {
    // Find node in map, update proper fields, then check for errors and update background color accordingly
    const node = props.colDef?.codeToNode?.[code];

    props.node.setDataValue(codeField, code);
    props.node.setDataValue(valueField, node?.id ?? null);
    const hasError = props.colDef?.hasError?.(props);
    props.eGridCell.style.backgroundColor = hasError ? '#ffcccc' : 'transparent';
    setName(node?.name ? `${code} (${node?.name})` : code);
  };

  return (
    props && (
      <Stack sx={{ height: '100%' }} justifyContent={'space-between'} direction="row" alignItems="center" spacing={1}>
        {name}
        <FontAwesomeIcon
          style={{ cursor: 'pointer', marginLeft: 'auto' }}
          icon={fas.faSearch}
          onClick={openEntitySelectDialog}
          color="gray"
        />
      </Stack>
    )
  );
};
export const MassOverwriteDialog = ({
  kpiCodes,
  closeDialog,
  entity: entityProp,
  defaultDimValues,
  augDimFieldDef,
}) => {
  const module = useModuleFromRoute();

  const routeEntity = useEntityFromRoute();
  const entity = entityProp ?? routeEntity;

  const rsrc = useRsrc();

  const perspectiveDef = useRef();

  const dimFieldToFieldDefRef = useRef({});
  const dimsFormRef = useRef();
  const [dimFormFieldDefs, setDimFieldFormDefs] = useState();
  const [dimFormValues, setDimFormValues] = useState({});

  const fieldToFieldDefRef = useRef({});

  const [step, setStep] = useState(0);
  const [selectedKpiCode, setSelectedKpiCode] = useState(() => kpiCodes[0]);
  const actionPluginCode = 'MASS_OVERWRITE_ACTION_PLUGIN';
  const { openDialog } = useDialog();
  const gridRef = useRef();

  useEffect(() => {
    if (!selectedKpiCode) {
      setSelectedKpiCode(kpiCodes[0]);
    }
  }, [kpiCodes, selectedKpiCode]);

  useEffect(() => {
    Promise.all([
      rsrc.data(module, 'PERSPECTIVE_DEF_DATA_PLUGIN', { entity }, entity),
      rsrc.fieldDefs(module, entity),
      rsrc.pluginFieldDefs(module, actionPluginCode, entity),
    ]).then(([perspectiveData, fieldDefs, pluginFieldDefs]) => {
      perspectiveDef.current = perspectiveData;

      fieldToFieldDefRef.current = Object.fromEntries(fieldDefs.map((fieldDef) => [fieldDef.field, fieldDef]));

      const dimFieldToFieldDef = {};
      Object.entries(perspectiveData.codeToDim).forEach(([dimCode, dim]) => {
        if (defaultDimValues?.[dimCode]) {
          return;
        }

        const field = `${StringUtils.toLowerCamel(dimCode)}Id`;
        const fieldDef = fieldToFieldDefRef.current[field];
        if (!fieldDef) {
          return;
        }

        let { leafIds } = TreeUtils.parseTree(dim.tree);
        const baseExpr = ExprUtils.whereParams('id', leafIds);

        fieldDef.dimCode = dimCode;
        fieldDef.expr = baseExpr;
        fieldDef.required = true;

        dimFieldToFieldDef[field] = fieldDef;

        augDimFieldDef?.(fieldDef, {
          dimCode: fieldDef.dimCode,
          selectedKpiCode,
          baseExpr,
        });

        // Have to build codeToNode map for EntitySelectCellRenderer to work properly,
        // since it needs to be able to look up the id of the selected entity by code we bring them into memory.
        // Allows for the same entities returned by entitySelect to be pasted in.
        fieldDef.codeToNode = {};
        rsrc.rows(module, fieldDef.entity, { expr: fieldDef.expr }).then((rows) => {
          rows.forEach((row) => {
            let node = { id: row.id, code: row.code, name: row.name };
            fieldDef.codeToNode[row.code] = node;
          });
        });
      });

      dimFieldToFieldDefRef.current = dimFieldToFieldDef;

      const dimFieldDefs = Object.values(dimFieldToFieldDef).map((dimFieldDef) => ({
        ...dimFieldDef,
        required: false,
      }));

      setDimFieldFormDefs([...pluginFieldDefs, ...dimFieldDefs]);
    });
  }, [module, entity, selectedKpiCode]);

  const createColDefs = (fieldDef, valueField) => {
    let touchedColDef = {
      ...fieldDef,
      field: fieldDef.field + 'Touched',
      cellRenderer: 'NONE',
      hide: true,
    };

    let newColDef = {
      ...fieldDef,
      valueField: valueField ?? fieldDef.field,
      touchedField: touchedColDef.field,
      setCellTouched: (props) => {
        props.node.setDataValue(props.colDef.touchedField, true);
      },
      isTouched: (props) => {
        let touched = !!props?.node.data?.[props.colDef.touchedField];
        return touched;
      },
    };

    return [newColDef, touchedColDef];
  };

  const kpiFieldDef = useMemo(() => {
    if (!selectedKpiCode || !dimFormFieldDefs) {
      return {};
    }

    const kpiLowerCamel = StringUtils.toLowerCamel(selectedKpiCode);
    const baseKpiFieldDef = fieldToFieldDefRef.current[kpiLowerCamel];
    if (!baseKpiFieldDef) {
      return {};
    }

    const kpiDef = {
      ...baseKpiFieldDef,
      required: true,
      precedence: 1000,
      editable: true,
      cellRenderer: ErrorCellRenderer,
      hasError: (params) => {
        let value = Number(params?.node.data?.[params.colDef.valueField]);

        let isNull = Number.isNaN(value);
        let touched = params.colDef?.isTouched?.(params);
        return isNull && touched;
      },
    };
    return kpiDef;
  }, [selectedKpiCode, dimFormFieldDefs]);

  const rowFieldDefs = useMemo(() => {
    if (!dimFormFieldDefs) return;

    const dimFieldToFieldDef = dimFieldToFieldDefRef.current;

    const nextFieldDefs = [...createColDefs(kpiFieldDef)];

    Object.entries(dimFieldToFieldDef).forEach(([fieldId, fieldDef]) => {
      if (dimFormValues[fieldId]) {
        return;
      }
      if (fieldDef.field.endsWith('Id')) {
        // Create code colDef for entity select, and hide the id colDef since we don't want to show it in the grid.
        let codeColDef = {
          ...fieldDef,
          field: fieldId.replace(/Id$/, 'Code'),
          headerName: fieldDef.displayName.replace(/Id$/, ' Code'),
          cellRenderer: EntitySelectCellRenderer,
          hasError: (params) => {
            let value = params?.node.data?.[params.colDef.valueField];
            let isNull = value === undefined || value == null || value === '';
            let touched = params.colDef?.isTouched?.(params);
            return isNull && touched;
          },
        };

        nextFieldDefs.push(...createColDefs(codeColDef, fieldDef.field));

        nextFieldDefs.push({
          field: fieldDef.field,
          hide: true,
          type: undefined,
          cellRenderer: 'NONE',
        });
      } else {
        nextFieldDefs.push(fieldDef);
      }
    });

    return nextFieldDefs.sort((a, b) => (a.precedence ?? 0) - (b.precedence ?? 0));
  }, [dimFormFieldDefs, dimFormValues, selectedKpiCode]);

  const clickNext = () => {
    if (dimsFormRef.current.hasErrorState()) {
      dimsFormRef.current.touchAll();
      return;
    }

    setStep(1);
  };

  const clickPrev = () => {
    setStep(0);
  };

  const submit = () => {
    const gridApi = getGridApi();
    const rows = [];
    const nodes = [];
    gridApi.forEachNode((node) => {
      nodes.push(node);
      rows.push(node.data);
    });
    touchNodes(nodes);
    let colDefs = gridApi.getAllDisplayedColumns().map((col) => col.getColDef());
    let hasError = !nodes.every((node) => {
      let errorArray = colDefs.map((colDef) => (colDef.hasError ? colDef.hasError({ node, colDef }) : false));
      return errorArray.every((error) => error === false);
    });
    if (hasError) {
      return;
    }

    const actionReq = {
      comment: dimFormValues.comment,
      dims: dimFormValues,
      defaultDimValues,
      kpiCode: selectedKpiCode,
      rows: rows,
    };

    const job = {
      code: actionPluginCode.replace('_ACTION_PLUGIN', ''),
      module,
      name: `${perspectiveDef.current.codeToKpi[selectedKpiCode].displayName} Overwrite`,
      entity,
      rsrcCode: actionPluginCode,
      reqParamJson: JSON.stringify(actionReq),
      cat: 'ACTION',
    };

    rsrc.save('skdr', 'JOB', job).then((job) => {
      rsrc.start(job.id);
      closeDialog();
    });
  };

  const buttons = useMemo(() => {
    let buttons = [
      <Button color="primary" variant="outlined" onClick={closeDialog}>
        Cancel
      </Button>,
    ];

    if (step === 0) {
      buttons.push(
        <Button color="primary" variant="contained" onClick={clickNext}>
          Next
        </Button>,
      );
    }

    if (step === 1) {
      buttons.push(
        <Button color="primary" variant="outlined" onClick={clickPrev}>
          Previous
        </Button>,
        <Button color="primary" variant="contained" onClick={submit}>
          Submit
        </Button>,
      );
    }
    return buttons;
  }, [step]);

  const getGridApi = () => gridRef.current?.gridApiRef?.current;

  const handlePaste = useCallback((clipboardText) => {
    const gridApi = getGridApi();

    const columns = gridApi.getAllDisplayedColumns();
    const parsedRows = parsePastedText(clipboardText);

    if (!parsedRows.length || !columns.length) return;

    const colMapping = columns.slice(0, parsedRows[0].length);
    const newRows = parsedRows
      .filter((cells) => cells.some((c) => c.trim() !== ''))
      .map((cells) => {
        const row = {};
        cells.forEach((value, i) => {
          const field = colMapping?.[i]?.getColDef().field;
          if (field) row[field] = value.trim();
        });
        return row;
      });

    if (newRows.length) {
      addRows(newRows, true);
    }
  }, []);

  const touchNodes = (nodes) => {
    const gridApi = getGridApi();
    let columns = [];
    let fieldDefs = gridApi.getAllDisplayedColumns().map((col) => col.getColDef());
    nodes.forEach((node) => {
      fieldDefs.forEach((colDef) => {
        if (colDef.setCellTouched) {
          columns.push(colDef.field);
          colDef.setCellTouched({ node, colDef });
        }
      });
    });
    gridApi?.refreshCells({ rowNodes: nodes, columns: columns, force: true });
  };

  const addRows = (newRows, touched = false) => {
    const gridApi = getGridApi();
    let res = gridApi?.applyTransaction({ add: newRows });
    if (touched) {
      touchNodes(res?.add);
    }
  };

  const addEmptyRow = useCallback(() => {
    addRows([{}], false);
  }, []);

  const deleteSelectedRows = useCallback(() => {
    const gridApi = getGridApi();
    if (gridApi) {
      const selectedRows = gridApi.getSelectedRows();
      gridApi.applyTransaction({ remove: selectedRows });
    }
  }, []);

  const titleButtons = useMemo(() => {
    if (step === 0) return;
    return (
      <>
        <TitleButton icon={fas.faPlus} toolTip="Add row" onClick={addEmptyRow} />
        <TitleButton
          icon={fas.faListCheck}
          toolTip="Paste from spreadsheet"
          onClick={() => {
            openDialog(PasteDataDialog, {}).then((text) => {
              if (text) handlePaste(text);
            });
          }}
        />
      </>
    );
  }, [step]);

  return (
    <KrDialog
      title={
        perspectiveDef.current && selectedKpiCode ?
          `${perspectiveDef.current.codeToKpi[selectedKpiCode].displayName} Overwrite`
        : ''
      }
      subtitle={
        step === 0 ?
          'Choose which dimensions to apply to all adjustments.'
        : 'Add one or more adjustment rows and fill in all required fields.'
      }
      titleButtons={titleButtons}
      fullWidth
      maxWidth={step === 0 ? 'md' : 'lg'}
      closeDialog={() => closeDialog()}
      buttons={buttons}
    >
      <Stepper activeStep={step}>
        <Step>
          <StepLabel>Select Dimensions</StepLabel>
        </Step>
        <Step>
          <StepLabel>Add Adjustments</StepLabel>
        </Step>
      </Stepper>

      {/* Dimensions Form */}
      {step === 0 && selectedKpiCode && dimFormFieldDefs && (
        <>
          {/* KPI Selection */}
          {kpiCodes.length > 1 && perspectiveDef.current && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>KPI</InputLabel>
                <Select
                  label="KPI"
                  value={selectedKpiCode}
                  onChange={(event) => setSelectedKpiCode(event.target.value)}
                >
                  {kpiCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {perspectiveDef.current.codeToKpi[code]?.displayName ?? code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Form key={selectedKpiCode} ref={dimsFormRef} formValues={dimFormValues} setFormValues={setDimFormValues}>
            <FormFields fieldDefs={dimFormFieldDefs} container={DialogFormFieldContainer} />
          </Form>
        </>
      )}
      {/* Rows Form */}
      {step === 1 && rowFieldDefs && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '600px' }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <Grid
              ref={gridRef}
              events={{
                onGridReady: (params) => {
                  gridRef.current?.gridApiRef?.current.setGridOption('columnDefs', rowFieldDefs);
                  gridRef.current.setDataSource([]);
                },
              }}
              gridOptions={{
                fieldDefs: rowFieldDefs,
                rowModelType: 'clientSide',
                rowSelection: 'multiple',
                onCellEditingStopped: (event) => {
                  if (event.colDef.setCellTouched) {
                    event.colDef.setCellTouched(event);
                    event.api.refreshCells({ rowNodes: [event.node], columns: [event.colDef.field], force: true });
                  }
                },
                getContextMenuItems: (params) => {
                  if (!params.node) return [];
                  return [
                    {
                      name: 'Delete Selected Rows',
                      action: deleteSelectedRows,
                      icon: fas.faTrash,
                    },
                    {
                      name: 'Add Row',
                      action: addEmptyRow,
                      icon: fas.faPlus,
                    },
                  ];
                },
              }}
            ></Grid>
          </Box>
        </Box>
      )}
    </KrDialog>
  );
};
